import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { SchoolInfo } from "@/types";
import { formatRank, formatScore, reportTitle, type ClassReport, type StudentResult } from "@/services/gradeService";
import { drawImageContain, drawImageCover, loadPdfImage, pdfDate, type PdfImage } from "./pdfImages";

export const GREEN: [number, number, number] = [29, 95, 208];
export const TEXT: [number, number, number] = [40, 40, 38];
export const MUTED: [number, number, number] = [110, 110, 106];
export const LIGHT: [number, number, number] = [236, 242, 238];

export const finalY = (doc: jsPDF, fallback: number) =>
  (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? fallback;

/** Nom court d'une évaluation pour une colonne : « Évaluation d'octobre » → « Octobre ». */
export function shortEvaluationName(title: string): string {
  const match = /^(?:évaluation|evaluation|éval\.?|composition|devoir)\s+(?:de\s+|d['’]\s*|du\s+)?(.+)$/i.exec(title.trim());
  const name = match ? match[1] : title.trim();
  const short = name.charAt(0).toUpperCase() + name.slice(1);
  return short.length > 14 ? `${short.slice(0, 13)}…` : short;
}

/** En-tête commun des documents de notes : logo, nom et coordonnées de l'école, année et période. */
export function drawSchoolHeader(doc: jsPDF, school: SchoolInfo, logo: PdfImage | null, right: string[], pageW: number, margin: number): number {
  let headerX = margin;
  if (logo) {
    drawImageContain(doc, logo, margin, 10, 20, 20);
    headerX = margin + 24;
  }
  doc.setTextColor(...GREEN);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(doc.splitTextToSize(school.name.toUpperCase(), pageW - margin * 2 - 70), headerX, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  [
    [school.address, school.city, school.country].filter(Boolean).join(", "),
    [school.phone && `Tél. : ${school.phone}`, school.email].filter(Boolean).join("  ·  "),
  ]
    .filter(Boolean)
    .forEach((line, i) => doc.text(line, headerX, 22 + i * 4));
  doc.setTextColor(...TEXT);
  right.forEach((line, i) => {
    doc.setFont("helvetica", i === 0 ? "bold" : "normal");
    doc.setFontSize(9);
    doc.text(line, pageW - margin, 15 + i * 5, { align: "right" });
  });
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.7);
  doc.line(margin, 33, pageW - margin, 33);
  return 33;
}

/** Un bulletin (une page A4) : d'une évaluation, ou du trimestre avec une colonne par évaluation. */
function drawReportCard(
  doc: jsPDF,
  report: ClassReport,
  student: StudentResult,
  school: SchoolInfo,
  logo: PdfImage | null,
  photo: PdfImage | null,
): void {
  const pageW = 210;
  const margin = 14;
  const scale = report.gradeScale;
  const isPeriod = report.scope === "PERIOD";

  drawSchoolHeader(doc, school, logo, [`Année scolaire ${report.period.schoolYear}`, report.period.name], pageW, margin);

  // Titre.
  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(isPeriod ? 15 : 13);
  doc.text(
    isPeriod ? `BULLETIN DE NOTES — ${report.period.name.toUpperCase()}` : `BULLETIN DE NOTES — ${reportTitle(report).toUpperCase()}`,
    pageW / 2,
    42,
    { align: "center" },
  );

  // Bloc élève.
  const boxY = 47;
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, boxY, pageW - margin * 2, 26, 2, 2, "F");
  let infoX = margin + 4;
  if (photo) {
    drawImageCover(doc, photo, margin + 3, boxY + 2, 18, 22);
    infoX = margin + 25;
  }
  const female = student.gender === "F";
  doc.setFontSize(11);
  doc.text(`${student.lastName.toUpperCase()} ${student.firstName}`, infoX, boxY + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`Matricule : ${student.matricule}`, infoX, boxY + 14);
  if (student.birthDate) doc.text(`${female ? "Née" : "Né"} le ${pdfDate(student.birthDate)}`, infoX, boxY + 19);
  doc.setTextColor(...TEXT);
  const rightX = pageW - margin - 4;
  doc.text(`Classe : ${report.className}`, rightX, boxY + 8, { align: "right" });
  doc.text(`Effectif : ${report.stats.count} élève${report.stats.count > 1 ? "s" : ""}`, rightX, boxY + 14, { align: "right" });
  if (report.titularName) doc.text(`Titulaire : ${report.titularName}`, rightX, boxY + 19, { align: "right" });

  // Notes par matière : au trimestre, une colonne par évaluation puis la moyenne du trimestre.
  const bySubject = new Map(student.subjects.map((s) => [s.subjectId, s]));
  const evalHeads = isPeriod ? report.evaluations.map((e) => shortEvaluationName(e.title)) : [];
  const head = [
    "Matière",
    ...evalHeads,
    isPeriod ? `Moy. trim. / ${scale}` : `Note / ${scale}`,
    "Coef.",
    "Points",
    "Moy. classe",
    "Appréciation",
  ];
  const body = report.subjects.map((subject) => {
    const r = bySubject.get(subject.subjectId);
    return [
      subject.name,
      ...(isPeriod ? report.evaluations.map((_, i) => formatScore(r?.scores[i] ?? null)) : []),
      formatScore(r?.average ?? null),
      String(subject.coefficient),
      formatScore(r?.points ?? null),
      formatScore(subject.classAverage),
      r?.mention ?? (r?.count ? "" : "Non évalué"),
    ];
  });
  const gradedCoefficients = report.subjects.reduce(
    (sum, s) => sum + ((bySubject.get(s.subjectId)?.average ?? null) !== null ? s.coefficient : 0),
    0,
  );
  const foot = [
    [
      "Moyenne générale",
      ...(isPeriod ? student.evaluationAverages.map((v) => formatScore(v)) : []),
      formatScore(student.average),
      String(gradedCoefficients),
      formatScore(student.totalPoints),
      formatScore(report.stats.average),
      "",
    ],
  ];
  const first = 1 + evalHeads.length; // colonne de la moyenne
  autoTable(doc, {
    startY: boxY + 31,
    margin: { left: margin, right: margin },
    head: [head],
    body,
    foot,
    theme: "grid",
    styles: { font: "helvetica", fontSize: evalHeads.length > 2 ? 8 : 9, cellPadding: 1.8, textColor: TEXT, lineColor: [210, 214, 208], lineWidth: 0.2 },
    headStyles: { fillColor: GREEN, textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
    footStyles: { fillColor: LIGHT, textColor: TEXT, fontStyle: "bold" },
    // Ligne « Moyenne générale » : chiffres alignés comme ceux des colonnes.
    didParseCell: (cell) => {
      if (cell.section === "foot" && cell.column.index > 0) cell.cell.styles.halign = cell.column.index === first + 1 ? "center" : "right";
    },
    columnStyles: {
      0: { cellWidth: isPeriod ? 42 : 52, halign: "left" },
      ...Object.fromEntries(evalHeads.map((_, i) => [i + 1, { halign: "right" as const, textColor: MUTED }])),
      [first]: { halign: "right", fontStyle: "bold" },
      [first + 1]: { halign: "center" },
      [first + 2]: { halign: "right" },
      [first + 3]: { halign: "right", textColor: MUTED },
    },
  });

  // Résultats.
  let y = finalY(doc, 150) + 6;
  const cell = (label: string, value: string, x: number, w: number) => {
    doc.setDrawColor(210, 214, 208);
    doc.setLineWidth(0.2);
    doc.rect(x, y, w, 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, x + w / 2, y + 5, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...TEXT);
    doc.text(value, x + w / 2, y + 12, { align: "center" });
  };
  const w = (pageW - margin * 2) / 4;
  cell(isPeriod ? "Moyenne du trimestre" : "Moyenne générale", student.average === null ? "—" : `${formatScore(student.average)} / ${scale}`, margin, w);
  cell("Rang", `${formatRank(student.rank, student.rankTied, student.gender)} / ${report.stats.graded}`, margin + w, w);
  cell("Mention", student.mention ?? "—", margin + w * 2, w);
  cell("Moyenne de la classe", formatScore(report.stats.average), margin + w * 3, w);
  y += 19;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(
    `Plus forte moyenne : ${formatScore(report.stats.highest)}  ·  Plus faible : ${formatScore(report.stats.lowest)}  ·  Moyenne d'admission : ${formatScore(report.passingAverage)}`,
    margin,
    y,
  );
  if (isPeriod && report.evaluations.length > 0) {
    y += 4;
    doc.text(
      `Moyenne du trimestre dans chaque matière = moyenne des évaluations : ${report.evaluations.map((e) => `${e.title} (${pdfDate(e.date)})`).join(", ")}.`,
      margin,
      y,
      { maxWidth: pageW - margin * 2 },
    );
  }

  // Appréciation générale (bulletin du trimestre).
  y += 6;
  doc.setDrawColor(210, 214, 208);
  doc.rect(margin, y, pageW - margin * 2, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...TEXT);
  doc.text("Appréciation générale", margin + 3, y + 5.5);
  doc.setFont("helvetica", "normal");
  if (student.remark) doc.text(doc.splitTextToSize(student.remark, pageW - margin * 2 - 6), margin + 3, y + 11);

  // Signatures.
  y += 28;
  const half = (pageW - margin * 2 - 6) / 2;
  const signature = (label: string, name: string | null, x: number) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(label, x + half / 2, y, { align: "center" });
    doc.rect(x, y + 3, half, 22);
    if (name) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(name, x + half / 2, y + 23, { align: "center" });
    }
  };
  signature(report.titularName ? "Le titulaire" : "L'enseignant principal", report.titularName, margin);
  signature(school.signatoryTitle?.trim() || "Le Directeur", school.signatoryName?.trim() || null, margin + half + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(`Bulletin édité le ${pdfDate(new Date().toISOString())} — ${school.name}`, pageW / 2, 288, { align: "center" });
}

/** Bulletins d'une classe (ou d'élèves choisis), d'une évaluation ou du trimestre : un par page A4. */
export async function exportReportCardsPdf(report: ClassReport, school: SchoolInfo, enrollmentIds?: string[]): Promise<void> {
  const students = enrollmentIds ? report.students.filter((s) => enrollmentIds.includes(s.enrollmentId)) : report.students;
  if (students.length === 0) return;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const logo = await loadPdfImage(school.logo);
  for (const [index, student] of students.entries()) {
    if (index > 0) doc.addPage();
    drawReportCard(doc, report, student, school, logo, await loadPdfImage(student.photoUrl));
  }
  const what = reportTitle(report);
  const name =
    students.length === 1
      ? `Bulletin-${students[0].lastName}-${students[0].firstName}-${what}`
      : `Bulletins-${report.className}-${what}`;
  doc.save(`${name.replace(/[\s'’/—]+/g, "-")}.pdf`);
}
